import { useMutation } from '@apollo/client';

import { apolloClientContracts } from '@/apollo/clients/contracts.apollo';
import {
  GetContractDetails,
  GetContractDetailsVariables,
} from '@/graphql/contracts/_generated_/getContractDetails.query';
import { PauseBilling, PauseBillingVariables } from '@/graphql/contracts/_generated_/pauseBilling.mutation';
import GetContractDetailsQuery from '@/graphql/contracts/getContractDetails.query.gql';
import PauseBillingMutation from '@/graphql/contracts/pauseBilling.mutation.gql';
import { notificationService } from '@/services/notification/notification.client';
import {
  ALL_ITEMS
} from '@/config/common.config';
import GetBCContractLinesQuery from '@/graphql/contracts/getPaginatedFilteredBCContractLines.query.gql';

export type PauseBillingParams = Omit<PauseBillingVariables, 'contractId'>;

export default function usePauseBilling(contractId: UUID) {
  const [_pauseBilling, { loading, error }] = useMutation<PauseBilling, PauseBillingVariables>(
    PauseBillingMutation,
    {
      client: apolloClientContracts,
      onError: (err) => notificationService.open({
        title: 'Error',
        text: err.message,
        type: 'danger'
      }),
      update: (_, { data }) => {
        if (!data) return;

        const cachedContract = apolloClientContracts.readQuery<GetContractDetails, GetContractDetailsVariables>({
          query: GetContractDetailsQuery,
          variables: { contractId }
        });

        const updatedContract: Contract = {
          ...cachedContract!.contract,
          billing: data.pauseBilling.billing
        };

        apolloClientContracts.writeQuery<GetContractDetails, GetContractDetailsVariables>({
          query: GetContractDetailsQuery,
          variables: { contractId },
          data: {
            contract: updatedContract
          }
        });
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GetBCContractLinesQuery,
          variables: {
            filter: {
              contractId: { eq: contractId }
            },
            first: ALL_ITEMS
          }
        }
      ],
      onCompleted: () => {
        notificationService.open({
          title: 'Pause billing',
          text: 'The contract has been successfully paused',
          type: 'success'
        });
      }
    }
  );

  const pauseBilling = (inputParams: PauseBillingParams) => {
    _pauseBilling({
      variables: {
        contractId,
        ...inputParams
      }
    });
  };

  return {
    pauseBilling,
    isLoadingPauseBilling: loading,
    errorPauseBilling: error,
  } as const;
}
